// Reading web pages and looking at pictures in chat: the assistant's read_webpage and look_at_images tools.
import { z } from "zod";
import { DocumentLookField, type DocumentPages, DocumentPagesField } from "./documents";

export const ReadWebpageInput = z.object({
  url: z.string().describe("Full http(s) address of the page, e.g. https://example.com/article."),
  offset: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe("Where to continue in the page text: the nextOffset of the previous part. Omit for the start."),
  // a link to a PDF is read like an attached document
  pages: DocumentPagesField,
  look: DocumentLookField,
});
export type ReadWebpageInput = z.infer<typeof ReadWebpageInput>;

/** A picture on a page, as listed beside its text. */
export interface PageImage {
  /** Its number in the page text: "(image N: …)". */
  n: number;
  url: string;
  alt: string | null;
  /** The figure caption, or the picture's title. */
  caption: string | null;
  /** The size the page declares, when it does. */
  width: number | null;
  height: number | null;
}

export const LookAtImagesInput = z.object({
  urls: z
    .array(z.string())
    .min(1)
    .max(4)
    .describe("Addresses of up to 4 pictures: links to image files, or urls from a page's images list."),
});
export type LookAtImagesInput = z.infer<typeof LookAtImagesInput>;

export interface LookedImage {
  url: string;
  /** Size of the original picture. */
  width: number | null;
  height: number | null;
  /** Its file type (jpeg, png, webp…). */
  type: string | null;
  /** Why this one could not be shown, else null. */
  error: string | null;
}

/** The pictures the assistant asked to see; the studio shows those in shownImages right after this result. */
export interface LookAtImagesOutput {
  images: LookedImage[];
  shownImages: string[];
}

/** One part of a page's readable text. */
export interface ReadWebpageOutput {
  /** Address after redirects. */
  url: string;
  title: string | null;
  siteName: string | null;
  byline: string | null;
  /** Main text as Markdown. */
  content: string;
  offset: number;
  /** Where the next part starts, or null when this part ends the page. */
  nextOffset: number | null;
  totalChars: number;
  /** Something the assistant should know (the page was cut at the size limit). */
  note: string | null;
  /** Set when the link is a PDF: the part is its pages rather than an offset into page text. */
  document?: DocumentPages | null;
  /** The page's pictures (first part only), numbered as in the text. */
  images?: PageImage[];
  /** Pictures shown to the assistant right after this result (a link straight to a picture). */
  shownImages?: string[];
}
